import { useMutation } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '../../lib/cn.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { Icon } from '../Icon.js'
import { Button } from '../ui.js'

/**
 * A bug report or a feature request, drafted for the person from what just
 * happened in the conversation — an error, a thumbs-down, something Eco could
 * not do — and sent only when they press Send. It files into the bug reports
 * (decision 117), the list Peter's bug sessions work through, and the person
 * follows it in Help → Your reports. Eco's own `report_problem` card does the
 * same from the model's side. Decision 116, 2026-11-01.
 */
export function ReportDraft({
  kind: initialKind,
  summary: initialSummary,
  details: initialDetails,
  sessionId,
  path,
  onClose,
}: {
  kind: 'bug' | 'feature'
  summary: string
  details: string
  sessionId: string | null
  path: string
  onClose: () => void
}) {
  const trpc = useTRPC()
  const [kind, setKind] = useState(initialKind)
  const [summary, setSummary] = useState(initialSummary)
  const [details, setDetails] = useState(initialDetails)
  const send = useMutation(trpc.assistant.report.mutationOptions())
  const box = useRef<HTMLFormElement>(null)
  // It opens at the foot of the conversation; bring it into view.
  useEffect(() => box.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }), [])

  if (send.data) {
    return (
      <div role="status" className="flex items-start gap-2 rounded-lg border-l-[3px] border-brand-500 bg-brand-50 px-3 py-2.5 text-[13px] text-ink-800">
        <Icon name="check" className="mt-0.5 size-4 shrink-0 text-brand-700" />
        <p className="min-w-0 flex-1">
          Sent as <strong className="font-semibold">{kind === 'bug' ? 'bug' : 'request'} {send.data.number}</strong>. You can follow it in{' '}
          <Link to={`/help/bugs/${send.data.number}`} className="font-semibold text-brand-700 underline underline-offset-2">
            Help → Your reports
          </Link>
          .
        </p>
        <button type="button" onClick={onClose} aria-label="Close" className="text-ink-400 hover:text-ink-900">
          <Icon name="close" className="size-4" />
        </button>
      </div>
    )
  }

  const field = 'w-full rounded-md bg-white px-2.5 py-1.5 text-[13px] text-ink-900 ring-1 ring-ink-300 ring-inset outline-none focus:ring-2 focus:ring-brand-600'
  return (
    <form
      ref={box}
      aria-label="Send to the backlog"
      className="flex flex-col gap-2.5 rounded-lg border border-ink-200 bg-white px-3.5 py-3 shadow-[0_1px_2px_rgb(16_24_40/0.05)]"
      onSubmit={(event) => {
        event.preventDefault()
        send.mutate({ kind, summary, details: details.trim() || null, sessionId, path })
      }}
    >
      <div className="flex items-center gap-2">
        <Icon name="flag" className="size-4 text-ink-400" />
        <p className="flex-1 text-[13px] font-semibold text-ink-900">Send it to the team’s backlog?</p>
        <div className="flex gap-1" role="radiogroup" aria-label="Kind">
          {(['bug', 'feature'] as const).map((option) => (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={kind === option}
              onClick={() => setKind(option)}
              className={cn(
                'rounded-md px-2 py-0.5 text-[12px] font-medium ring-1 ring-inset',
                kind === option ? 'bg-ink-900 text-white ring-ink-900' : 'bg-white text-ink-700 ring-ink-300 hover:bg-ink-50',
              )}
            >
              {option === 'bug' ? 'Bug' : 'Feature request'}
            </button>
          ))}
        </div>
      </div>
      <label className="flex flex-col gap-1 text-[11.5px] font-medium text-ink-600">
        In a line
        <input value={summary} maxLength={200} onChange={(event) => setSummary(event.target.value)} className={field} />
      </label>
      <label className="flex flex-col gap-1 text-[11.5px] font-medium text-ink-600">
        {kind === 'bug' ? 'What happened' : 'What you need it to do'}
        <textarea value={details} rows={4} maxLength={3000} onChange={(event) => setDetails(event.target.value)} className={cn(field, 'resize-y leading-relaxed')} />
      </label>
      <p className="text-[11.5px] text-ink-500">The last few messages of this conversation go with it.</p>
      {send.error ? (
        <p role="alert" className="rounded-md bg-red-50 px-2.5 py-1.5 text-[12.5px] text-red-700">
          {errorMessage(send.error)}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" size="sm" variant="primary" loading={send.isPending} disabled={summary.trim().length < 3}>
          Send to the backlog
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Not now
        </Button>
      </div>
    </form>
  )
}
